<template>
  <div class="upload-component">
    <template v-if="!fileUrl || !showPreview || (batchMode && batchFileList.length === 0)">
      <!-- 上传方式选择 -->
      <div v-if="effectiveEnableCloudSelect" class="upload-mode-selector">
        <el-radio-group v-model="uploadMode" @change="handleUploadModeChange">
          <el-radio value="local">本地上传</el-radio>
          <el-radio value="cloud">云盘选择</el-radio>
          <el-radio v-if="enableUrlInput" value="url">网络连接</el-radio>
        </el-radio-group>
      </div>

      <!-- 本地上传区域 -->
      <div v-if="uploadMode === 'local'" class="upload-area" v-loading="loading">
        <el-upload
          ref="uploadRef"
          class="upload-demo"
          drag
          :action="uploadUrl"
          :headers="uploadHeaders"
          :data="{ folderId: effectiveFolderId }"
          :show-file-list="false"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :before-upload="beforeUpload"
          :on-change="handleFileChange"
          :on-progress="handleUploadProgress"
          :http-request="customUploadRequest"
          :accept="effectiveAccept"
          :limit="fileLimit"
          :multiple="multiple"
          :disabled="loading"
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            Drop file here or <em>click to upload</em>
          </div>
          <template #tip>
            <div class="el-upload__tip">
              <el-text type="info" size="small">{{ uploadTip }}</el-text>
            </div>
          </template>
        </el-upload>
        <el-progress
          v-if="uploadPercent > 0 && uploadPercent < 100"
          :percentage="uploadPercent"
          status="active"
          style="margin-top: 12px;"
        />
      </div>

      <!-- 云盘文件选择区域 -->
      <div v-if="uploadMode === 'cloud'" class="cloud-file-selector">
        <div class="cloud-selector-header">
          <el-button type="primary" @click="openCloudFileDialog" :loading="loading">
            <el-icon><FolderOpened /></el-icon>
            选择云盘文件
          </el-button>
          <el-text type="info" size="small" style="margin-left: 12px;">
            {{ cloudSelectorTip }}
          </el-text>
        </div>
      </div>

      <!-- 网络连接输入区域 -->
      <div v-if="uploadMode === 'url' && enableUrlInput" class="url-input-area">
        <div class="url-input-container">
          <el-input
            v-model="urlInput"
            placeholder="请输入文件的网络地址（URL）"
            clearable
            @clear="handleUrlClear"
          >
            <template #append>
              <el-button type="primary" @click="confirmUrlInput" :disabled="!urlInput" :loading="loading">
                确认
              </el-button>
            </template>
          </el-input>
          <el-text type="info" size="small" style="margin-top: 8px; display: block;">
            有效URL地址必须是 http:// 或 https:// 开头，且文件扩展名必须是{{ effectiveAccept }}）
          </el-text>
        </div>
      </div>
    </template>

    <template v-else-if="!batchMode && showPreview">
      <div class="file-preview">
        <slot name="preview" :fileUrl="fileUrl" :selectedFile="selectedCloudFile" :videoW="videoW" :videoH="videoH" :videoContainerRef="videoContainerRef" :onVideoLoadedMetadata="onVideoLoadedMetadata">
          <!-- 默认预览内容 -->
          <div v-if="previewType === 'audio'" class="audio-preview">
            <audio :src="fileUrl" controls preload="metadata" style="width: 100%;" />
            <div style="display: flex; justify-content: flex-end; width: 100%;">
              <el-button type="danger" size="small" @click="removeFile" style="margin-top: 8px;">移除文件</el-button>
            </div>
          </div>
          <div v-else class="video-preview">
            <div class="video-preview-relative" ref="videoContainerRef">
              <video :src="fileUrl" controls style="width:100%;max-height: 75vh;" @loadedmetadata="onVideoLoadedMetadata" />
            </div>
            <div style="display: flex; justify-content: space-between;align-items: center; width: 100%;">
              <el-text type="info" size="small">视频尺寸：{{ videoW }} x {{ videoH }}</el-text>
              <el-button type="danger" size="small" @click="removeFile" style="margin-top: 8px;">移除文件</el-button>
            </div>
          </div>
        </slot>
      </div>
    </template>

    <!-- 批量上传进度显示 -->
    <template v-else-if="batchMode && batchUploadProgress.length > 0">
      <div class="batch-upload-progress">
        <h4>上传进度：</h4>
        <div v-for="(item, index) in batchUploadProgress" :key="index" class="progress-item">
          <span class="filename">{{ item.name }}</span>
          <el-progress
            :percentage="item.percentage"
            :stroke-width="8"
          >
            <el-text :type="item.status" size="small">{{ item.statusText }}</el-text>
            <el-button
              v-if="item.status === 'uploading'"
              type="danger"
              size="small"
              @click="cancelSingleUpload(item, index)"
              :title="'取消上传'"
              link
            >
              <el-icon><Close /></el-icon>
            </el-button>
          </el-progress>

        </div>
      </div>
    </template>

    <!-- 云盘文件选择弹窗 -->
    <el-dialog
      v-model="cloudFileDialogVisible"
      :title="batchMode ? '批量选择云盘文件' : '选择云盘文件'"
      width="80%"
      :close-on-click-modal="false"
      class="cloud-file-dialog"
      draggable
      :top="'10vh'"
      :destroy-on-close="false"
      :append-to-body="true"
      :modal="true"
      :lock-scroll="false"
      :draggable="false"
    >
      <div class="cloud-file-dialog-content">
        <!-- 视频预览层：在弹窗内覆盖显示，无需第二个弹窗和 z-index -->
        <div v-if="cloudVideoPreviewVisible" class="cloud-video-preview-inner" @click.self="closeCloudVideoPreview">
          <div class="cloud-video-preview-inner-box" @click.stop>
            <div class="cloud-video-preview-inner-header">
              <span>视频预览</span>
              <el-button text type="primary" size="small" @click="closeCloudVideoPreview">
                <el-icon><Close /></el-icon>
              </el-button>
            </div>
            <video
              v-if="cloudVideoPreviewUrl"
              :src="cloudVideoPreviewUrl"
              controls
              style="width: 100%; max-height: 60vh;"
            />
          </div>
        </div>
        <!-- 左侧文件夹树 -->
        <div class="cloud-folder-tree">
          <div v-if="cloudFolderList.length === 0" class="empty-folder">
            <el-icon :size="60" color="#fff"><Folder /></el-icon>
            <div class="empty-text">暂无文件夹</div>
          </div>
          <el-tree
            v-else
            :data="cloudFolderList"
            :props="cloudTreeProps"
            node-key="id"
            default-expand-all
            highlight-current
            @node-click="handleCloudNodeClick"
            style="color: #fff;"
          >
            <template #default="{ node, data }">
              <span style="display: flex; align-items: center; gap: 8px;">
                <el-icon><Folder /></el-icon>
                {{ data.folderName }}
                <span style="font-size: 12px; color: #ccc;">{{ formatSize(data.folderSize) }}</span>
              </span>
            </template>
          </el-tree>
        </div>

        <!-- 右侧文件列表 -->
        <div class="cloud-file-list">
          <div class="cloud-file-header">
            <el-text>当前文件夹：{{ currentCloudFolderName || '根目录' }}</el-text>
            <div class="cloud-file-type-filters">
              <el-radio-group v-model="cloudFileTypeFilter" size="small" @change="handleCloudFileTypeFilterChange">
                <el-radio-button value="">全部</el-radio-button>
                <el-radio-button value="image">图片</el-radio-button>
                <el-radio-button value="audio">音频</el-radio-button>
                <el-radio-button value="video">视频</el-radio-button>
              </el-radio-group>
            </div>
          </div>
          <div class="table-container">
            <el-table
              :data="cloudFileList"
              style="width: 100%;"
              v-loading="cloudFileLoading"
              @row-click="handleCloudFileSelect"
              @selection-change="handleCloudFileSelectionChange"
              highlight-current-row
              :row-class-name="getRowClassName"
              ref="cloudFileTableRef"
              :max-height="600"
            >
              <el-table-column v-if="batchMode" type="selection" width="55" :selectable="(row) => isValidFile(row.fileName)" />
              <el-table-column label="预览" width="120">
                <template #default="scope">
                  <div class="cloud-thumb">
                    <div
                      v-if="isVideoFileType(scope.row.fileName) && scope.row.fileUrl"
                      class="cloud-thumb-video-wrap"
                      @click.stop="openCloudVideoPreview(scope.row.fileUrl)"
                    >
                      <video
                        :src="scope.row.fileUrl"
                        muted
                        loop
                        preload="metadata"
                        playsinline
                        @loadedmetadata="(event) => handleCloudThumbMetadata(scope.row, event)"
                      />
                    </div>
                    <img
                      v-else-if="isAudioFileType(scope.row.fileName)"
                      :src="waveImage"
                      alt="audio preview"
                      class="cloud-thumb-image"
                    />
                    <img
                      v-else-if="isImageFileType(scope.row.fileName)"
                      :src="scope.row.fileUrl"
                      alt="image preview"
                      class="cloud-thumb-image"
                    />
                    <div v-else class="cloud-thumb-icon">
                      <el-icon><Document /></el-icon>
                    </div>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="fileName" label="文件名" min-width="200">
                <template #default="scope">
                  <div style="display: flex; align-items: center;">
                    <el-icon style="margin-right: 8px;">
                      <Film v-if="isVideoFileType(scope.row.fileName)"/>
                      <Headset v-else-if="isAudioFileType(scope.row.fileName)" />
                      <Picture v-else-if="isImageFileType(scope.row.fileName)" />
                      <Document v-else />
                    </el-icon>
                    {{ scope.row.fileName }}
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="fileSize" label="文件大小" min-width="100">
                <template #default="scope">
                  {{ formatSize(scope.row.fileSize) }}
                </template>
              </el-table-column>
              <el-table-column label="时长" min-width="90" align="center">
                <template #default="scope">
                  {{ formatCloudVideoDuration(scope.row) }}
                </template>
              </el-table-column>
              <el-table-column prop="createDate" label="创建时间" min-width="160">
                <template #default="scope">
                  {{ formatDate(scope.row.createDate) }}
                </template>
              </el-table-column>
              <el-table-column label="操作" min-width="120">
                <template #default="scope">
                  <el-button
                    text
                    size="small"
                    type="primary"
                    @click.stop="handleSingleFileSelect(scope.row)"
                    :disabled="!isValidFile(scope.row.fileName) || (processingFileId && processingFileId.value !== null)"
                    :loading="processingFileId && processingFileId.value === scope.row.id"
                  >
                    选择
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- 分页 -->
          <div v-if="cloudFileTotal > 0" class="cloud-pagination">
            <el-pagination
              v-model:current-page="cloudCurrentPage"
              v-model:page-size="cloudPageSize"
              :page-sizes="[20 , 50, 100]"
              :total="cloudFileTotal"
              layout="total, sizes, prev, pager, next"
              @size-change="handleCloudPageSizeChange"
              @current-change="handleCloudPageChange"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <el-button @click="cloudFileDialogVisible = false">取消</el-button>
        <el-button
          v-if="!batchMode && !autoConfirmCloudSelect"
          type="primary"
          @click="confirmSelectCloudFile(selectedCloudFileInDialog)"
          :disabled="!selectedCloudFileInDialog || !isValidFile(selectedCloudFileInDialog.fileName)"
          :loading="loading"
        >
          确认选择
        </el-button>
        <el-button
          v-else-if="batchMode"
          type="primary"
          @click="confirmBatchSelectCloudFiles"
          :disabled="selectedCloudFiles.length === 0"
          :loading="loading"
        >
          批量选择 ({{ selectedCloudFiles.length }})
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { UploadFilled, FolderOpened, Folder, Film, Headset, Document, Close } from '@element-plus/icons-vue'
import { getUserFolderList, getFilesByFolder, downloadUserFile, getUserDalFolder } from '@/api/portal/user'
import { ensureEnoughCloudSpace } from '@/apps/portal/utils/cloudSpace'
import waveImage from '@/apps/portal/assets/wave.png'

const BLOCKED_EXTENSIONS = ['mov']

// Props定义
const props = defineProps({
  // 文件URL，用于显示已选择的文件
  modelValue: {
    type: [String, Array],
    default: ''
  },
  // 接受的文件类型
  accept: {
    type: String,
    default: '.mp4,.wav'
  },
  // 上传提示文本
  uploadTip: {
    type: String,
    default: 'mp4/wav 文件，5G以内'
  },
  // 云盘选择提示文本
  cloudSelectorTip: {
    type: String,
    default: '从云盘空间中选择mp4/wav文件'
  },
  // 最大文件大小（MB）
  maxSize: {
    type: Number,
    default: 5000
  },
  // 是否启用云盘选择
  enableCloudSelect: {
    type: Boolean,
    default: true
  },
  // 存储键前缀，用于localStorage
  storageKey: {
    type: String,
    default: 'upload'
  },
  // 是否支持批量上传
  multiple: {
    type: Boolean,
    default: false
  },
  // 文件数量限制
  fileLimit: {
    type: Number,
    default: 1
  },
  // 是否显示预览
  showPreview: {
    type: Boolean,
    default: true
  },
  // 单文件云盘选择时，点击文件行即完成选择；默认保持其他工具原有的确认流程。
  autoConfirmCloudSelect: {
    type: Boolean,
    default: false
  },
  // 默认预览类型：video 会展示视频尺寸；audio 仅展示音频播放器
  previewType: {
    type: String,
    default: 'video',
    validator: (value) => ['video', 'audio'].includes(value)
  },
  // 批量上传模式
  batchMode: {
    type: Boolean,
    default: false
  },
  // 自定义上传URL（已废弃，统一使用 COS 上传）
  customUploadUrl: {
    type: String,
    default: ''
  },
  // 是否需要腾讯云 URL（已废弃，统一使用 COS 上传）
  needCloudUrl: {
    type: Boolean,
    default: false
  },
  // 是否启用网络连接输入
  enableUrlInput: {
    type: Boolean,
    default: false
  },
  // 公开模式：无需登录（问卷分享页）
  publicMode: {
    type: Boolean,
    default: false
  },
  // 公开上传时关联的项目 ID
  projectId: {
    type: [String, Number],
    default: null
  },
  folderId: {
    type: [String, Number],
    default: null
  },
  // 自动任务素材上传会指定独立云盘文件夹，并把素材索引写入 videoDescription。
  videoDescription: {
    type: String,
    default: ''
  }
})

// Emits定义
const emit = defineEmits(['update:modelValue', 'batch-upload-success', 'file-name-change'])

// 内置配置
const effectiveEnableCloudSelect = computed(() => props.enableCloudSelect && !props.publicMode)
// 默认沿用用户缺省文件夹；自动任务素材可由父组件强制写入专用文件夹。
const effectiveFolderId = computed(() => props.folderId || folderId.value)

const uploadUrl = computed(() => {
  const base = import.meta.env.VITE_APP_API_BASE_URL
  if (props.publicMode) {
    return `${base}/pm/questionnaire/public/upload`
  }
  return `${base}/ksai/upload`
})

const uploadHeaders = computed(() => {
  if (props.publicMode) {
    return {}
  }
  return {
    token: localStorage.getItem('token') || ''
  }
})
const folderId = ref(0)

// 响应式数据
const uploadMode = ref('local')
const fileUrl = ref(props.modelValue)
const selectedCloudFile = ref(null)
const loading = ref(false)
const uploadPercent = ref(0)
const urlInput = ref('')
const uploadRef = ref(null) // el-upload组件的引用

// 批量上传相关数据
const batchFileList = ref([])
const batchUploadProgress = ref([])
const selectedCloudFiles = ref([])

// 上传请求跟踪
const activeUploadRequests = ref(new Map()) // 跟踪活跃的上传请求

// 视频预览相关数据
const videoW = ref(0)
const videoH = ref(0)
const videoContainerRef = ref(null)

// 云盘相关数据
const cloudFileDialogVisible = ref(false)
const cloudFolderList = ref([])
const cloudFileList = ref([])
const cloudFileLoading = ref(false)
const currentCloudFolderId = ref(null)
const currentCloudFolderName = ref('')
const selectedCloudFileInDialog = ref(null)
const cloudCurrentPage = ref(1)
const cloudPageSize = ref(20)
const cloudFileTotal = ref(0)
const cloudFileTableRef = ref(null)
const processingFileId = ref(null) // 跟踪当前正在处理的文件ID
const cloudFileTypeFilter = ref('') // '' | 'image' | 'audio' | 'video' 文件类型筛选
const cloudVideoPreviewVisible = ref(false) // 云盘视频预览弹窗
const cloudVideoPreviewUrl = ref('') // 当前预览的视频地址
// 云盘列表视频时长缓存：fileId -> 秒数
const cloudVideoDurationMap = ref({})

// 统一禁用 mov（即使上层 accept 传了 mov，这里也会过滤掉）
const effectiveAccept = computed(() => {
  const raw = String(props.accept || '')
  if (!raw) return raw
  return raw
    .split(',')
    .map(v => v.trim())
    .filter(v => {
      const ext = v.toLowerCase().replace('.', '')
      return ext && !BLOCKED_EXTENSIONS.includes(ext)
    })
    .join(',')
})

const cloudTreeProps = {
  children: 'children',
  label: 'folderName'
}

const isBlockedFileName = (fileName) => {
  if (!fileName) return false
  const lower = String(fileName).toLowerCase()
  return BLOCKED_EXTENSIONS.some(ext => lower.endsWith('.' + ext))
}

// 内置文件验证函数
const isValidFile = (fileName) => {
  if (!fileName) return false

  // 确保fileName是字符串类型
  const fileNameStr = String(fileName)
  if (!fileNameStr) return false

  const lowerName = fileNameStr.toLowerCase()

  // 禁止上传/选择 mov
  if (isBlockedFileName(lowerName)) {
    return false
  }

  // 根据accept属性解析允许的文件扩展名
  const acceptExtensions = effectiveAccept.value
    .split(',')
    .map(ext => ext.trim().toLowerCase().replace('.', ''))

  // 检查文件扩展名是否在允许的列表中
  return acceptExtensions.some(ext => lowerName.endsWith('.' + ext))
}

// 监听modelValue变化
watch(() => props.modelValue, (newVal) => {
  if (props.batchMode) {
    batchFileList.value = Array.isArray(newVal) ? newVal : []
  } else {
    fileUrl.value = newVal
  }
})

watch(() => props.folderId, (newVal) => {
  if (newVal) {
    folderId.value = Number(newVal) || 0
  }
})

// 监听fileUrl变化，同步到父组件
watch(fileUrl, (newVal) => {
  if (!props.batchMode) {
    emit('update:modelValue', newVal)
  }
})

// 监听批量文件列表变化
watch(batchFileList, (newVal) => {
  if (props.batchMode) {
    emit('update:modelValue', newVal)
  }
})

// 获取用户文件夹ID
const fetchUserFolder = async () => {
  // 公开问卷使用专用上传接口，不应读取需要登录的用户云盘目录。
  if (props.publicMode) {
    folderId.value = 0
    return
  }
  // 父组件传入 folderId 时不再请求默认文件夹，避免批量素材落到错误目录。
  if (props.folderId) {
    folderId.value = Number(props.folderId) || 0
    return
  }
  try {
    const res = await getUserDalFolder()
    folderId.value = res.folder.id || 0
    console.log('获取用户文件夹ID:', folderId.value)
  } catch (e) {
    console.error('获取用户文件夹失败:', e)
  }
}

// 组件挂载时获取文件夹ID
fetchUserFolder()

// 上传方式切换
const handleUploadModeChange = (mode) => {
  if (mode === 'cloud') {
    if (props.batchMode) {
      batchFileList.value = []
    } else if (!selectedCloudFile.value) {
      fileUrl.value = ''
    }
  } else if (mode === 'url') {
    // 切换到网络连接模式时，如果已有 URL，填充到输入框
    if (fileUrl.value && !props.batchMode) {
      urlInput.value = fileUrl.value
    } else {
      urlInput.value = ''
    }
  }
}

// URL 验证函数
const isValidUrl = (urlString) => {
  if (!urlString || typeof urlString !== 'string') return false

  try {
    const url = new URL(urlString.trim())
    // 检查协议是否为 http 或 https
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return false
    }

    // 获取 URL 路径（去除查询参数和哈希）
    const pathname = url.pathname.toLowerCase()

    // 禁止 mov
    if (isBlockedFileName(pathname)) {
      return false
    }

    // 根据accept属性解析允许的文件扩展名
    const acceptExtensions = effectiveAccept.value
      .split(',')
      .map(ext => ext.trim().toLowerCase().replace('.', ''))

    // 检查路径是否以允许的文件扩展名结尾
    return acceptExtensions.some(ext => pathname.endsWith('.' + ext))
  } catch (e) {
    // 如果 URL 构造函数抛出异常，说明不是有效的 URL
    return false
  }
}

// 确认 URL 输入
const confirmUrlInput = async () => {
  const trimmedUrl = urlInput.value.trim()

  if (!trimmedUrl) {
    ElMessage.warning('请输入 URL 地址')
    return
  }

  if (!isValidUrl(trimmedUrl)) {
    ElMessage.error('请输入有效的 URL 地址（必须以 http:// 或 https:// 开头，且文件扩展名必须是' + effectiveAccept.value + '；不支持 .mov）')
    return
  }

  if (props.batchMode) {
    ElMessage.warning('批量模式下不支持网络连接方式')
    return
  }

  selectedCloudFile.value = null

  // 直接赋值给 fileUrl
  fileUrl.value = trimmedUrl

  // 发送文件名事件（使用 URL 作为文件名）
  emit('file-name-change', { fileName: trimmedUrl, source: 'url' })

  ElMessage.success('URL 设置成功')
}


// 清除 URL 输入
const handleUrlClear = () => {
  if (!props.batchMode && fileUrl.value === urlInput.value) {
    fileUrl.value = ''
    selectedCloudFile.value = null
  }
}

// 本地上传相关方法
const beforeUpload = async (file) => {
  if (!props.publicMode) {
    const hasSpace = await ensureEnoughCloudSpace()
    if (!hasSpace) {
      return false
    }
    if (!effectiveFolderId.value) {
      await fetchUserFolder()
    }
    if (!effectiveFolderId.value) {
      ElMessage.error('云盘目录获取失败，请刷新后重试')
      return false
    }
  }
  if (file && isBlockedFileName(file.name)) {
    ElMessage.error('不支持上传 .mov 文件，请转换为 .mp4 后再上传')
    return false
  }
  const isLtMaxSize = file.size / 1024 / 1024 < props.maxSize
  if (!isLtMaxSize) {
    ElMessage.error(`文件大小不能超过 ${props.maxSize}MB!`)
  }
  return isLtMaxSize
}

// 自定义上传请求处理
const customUploadRequest = (options) => {
  const { file, onProgress, onSuccess, onError } = options

  // 创建XMLHttpRequest对象
  const xhr = new XMLHttpRequest()

  // 设置超时时间（20分钟）
  xhr.timeout = 1200000

  // 生成唯一ID用于跟踪请求
  const requestId = `${file.name}_${Date.now()}`

  // 将请求添加到跟踪Map中
  activeUploadRequests.value.set(requestId, xhr)

  // 在批量模式下，将请求信息存储到进度项中
  if (props.batchMode) {
    const progressItem = batchUploadProgress.value.find(item => item.name === file.name)
    if (progressItem) {
      progressItem.requestId = requestId
    }
  }

  // 监听上传进度
  xhr.upload.addEventListener('progress', (event) => {
    if (event.lengthComputable) {
      const percent = Math.round((event.loaded / event.total) * 100)
      onProgress({ percent })
    }
  })

  // 监听上传完成
  xhr.addEventListener('load', () => {
    // 从跟踪Map中移除
    activeUploadRequests.value.delete(requestId)

    // 在批量模式下，清除进度项中的请求信息
    if (props.batchMode) {
      const progressItem = batchUploadProgress.value.find(item => item.requestId === requestId)
      if (progressItem) {
        progressItem.requestId = null
        progressItem.abortFunction = null
      }
    }

    if (xhr.status === 200) {
      try {
        const response = JSON.parse(xhr.responseText)
        onSuccess(response)
      } catch (e) {
        onError(new Error('响应解析失败'))
      }
    } else {
      let errorMessage = `上传失败，状态码: ${xhr.status}`
      if (xhr.status === 413) {
        errorMessage = `文件过大，请选择小于 ${props.maxSize}MB 的文件`
      } else if (xhr.status === 408) {
        errorMessage = '上传超时，请检查网络连接后重试'
      } else if (xhr.status === 500) {
        errorMessage = '服务器内部错误，请稍后重试'
      }
      onError(new Error(errorMessage))
    }
  })

  // 监听上传错误
  xhr.addEventListener('error', (event) => {
    activeUploadRequests.value.delete(requestId)

    // 在批量模式下，清除进度项中的请求信息
    if (props.batchMode) {
      const progressItem = batchUploadProgress.value.find(item => item.requestId === requestId)
      if (progressItem) {
        progressItem.requestId = null
        progressItem.abortFunction = null
      }
    }

    // 检查是否是chunked encoding错误
    let errorMessage = '网络错误'
    if (event.type === 'error' && xhr.readyState === 4) {
      errorMessage = '文件传输中断，请检查网络连接后重试'
    }

    onError(new Error(errorMessage))
  })

  // 监听上传超时
  xhr.addEventListener('timeout', () => {
    activeUploadRequests.value.delete(requestId)

    // 在批量模式下，清除进度项中的请求信息
    if (props.batchMode) {
      const progressItem = batchUploadProgress.value.find(item => item.requestId === requestId)
      if (progressItem) {
        progressItem.requestId = null
        progressItem.abortFunction = null
        progressItem.status = 'exception'
        progressItem.statusText = '上传超时'
        progressItem.percentage = 0
      }
    }

    onError(new Error('上传超时，请检查网络连接后重试'))
  })

  // 监听上传中止
  xhr.addEventListener('abort', () => {
    activeUploadRequests.value.delete(requestId)

    // 在批量模式下，清除进度项中的请求信息
    if (props.batchMode) {
      const progressItem = batchUploadProgress.value.find(item => item.requestId === requestId)
      if (progressItem) {
        progressItem.requestId = null
        progressItem.abortFunction = null
        // 更新进度项状态为已取消
        progressItem.status = 'exception'
        progressItem.statusText = '上传已取消'
        progressItem.percentage = 0
      }
    }

    // 对于中止的上传，不调用 onError，因为这是用户主动取消的
    // 只在批量模式下更新状态，单文件模式下不需要特殊处理
  })

  // 准备FormData
  const formData = new FormData()
  formData.append('file', file)
  if (props.publicMode) {
    if (!props.projectId) {
      onError(new Error('项目ID不能为空'))
      return
    }
    formData.append('projectId', String(props.projectId))
  } else {
    formData.append('folderId', effectiveFolderId.value)
    // videoDescription 是后端 base_user_files.video_description 的来源，
    // 用于缓存 producer_engine 的素材索引，后续制片可跳过重复分析。
    if (props.videoDescription) {
      formData.append('videoDescription', props.videoDescription)
    }
  }

  // 发送请求
  xhr.open('POST', uploadUrl.value)
  const token = uploadHeaders.value.token
  if (token) {
    xhr.setRequestHeader('token', token)
  }

  // 设置额外的请求头以优化大文件上传
  xhr.setRequestHeader('Cache-Control', 'no-cache')
  xhr.setRequestHeader('Pragma', 'no-cache')

  // 发送请求
  xhr.send(formData)

  // 创建中止函数
  const abortFunction = () => {
    xhr.abort()
    activeUploadRequests.value.delete(requestId)

    // 在批量模式下，清除进度项中的请求信息
    if (props.batchMode) {
      const progressItem = batchUploadProgress.value.find(item => item.requestId === requestId)
      if (progressItem) {
        progressItem.requestId = null
        progressItem.abortFunction = null
      }
    }
  }

  // 在批量模式下，将中止函数存储到进度项中
  if (props.batchMode) {
    const progressItem = batchUploadProgress.value.find(item => item.name === file.name)
    if (progressItem) {
      progressItem.abortFunction = abortFunction
    }
  }

  // 返回中止函数
  return {
    abort: abortFunction
  }
}

const handleFileChange = (file, fileList) => {
  // 内部处理文件变化
  console.log('文件已选择:', file, fileList)

  // 检查文件数量是否超过限制
  if (props.batchMode && fileList.length > props.fileLimit) {
    ElMessage.error(`文件数量不能超过 ${props.fileLimit} 个，当前已选择 ${fileList.length} 个文件`)
    // 移除超出限制的文件
    uploadRef.value.handleRemove(file)
    return
  }

  if (props.batchMode) {
    // 批量上传模式，为每个文件创建进度项
    const existingProgress = batchUploadProgress.value.find(item => item.name === file.name)
    if (!existingProgress) {
      batchUploadProgress.value.push({
        name: file.name,
        percentage: 0,
        status: 'uploading',
        statusText: '准备上传',
        file: file,
        requestId: null, // 用于跟踪上传请求
        abortFunction: null // 用于取消上传的函数
      })
    }
  }
}

const handleUploadProgress = (event, file, fileList) => {
  loading.value = true

  if (props.batchMode) {
    // 批量上传模式，更新对应文件的进度
    const progressItem = batchUploadProgress.value.find(item => item.name === file.name)
    if (progressItem) {
      progressItem.percentage = Math.min(Math.floor(event.percent), 99)
      progressItem.statusText = '上传中...'
    }
  } else {
    // 单文件上传模式
    uploadPercent.value = Math.min(Math.floor(event.percent), 99)
  }
}

// 处理上传错误
const handleUploadError = (error, file, fileList) => {
  console.log('handleUploadError 被调用:', error, file.name)

  ElMessage.error('上传失败: ' + (error.message || error))
  loading.value = false
  uploadPercent.value = 0

  // 在批量模式下，更新对应文件的进度状态
  if (props.batchMode) {
    const progressItem = batchUploadProgress.value.find(item => item.name === file.name)
    if (progressItem) {
      progressItem.status = 'exception'
      progressItem.statusText = '上传失败'
      progressItem.percentage = 0
    }
  }
}

const handleUploadSuccess = (res, file, fileList) => {
  console.log('handleUploadSuccess 被调用:', res, file.name)

  // 检查响应是否成功
  if (res.code !== 0) {
    ElMessage.error(res.msg || '上传失败')
    loading.value = false
    uploadPercent.value = 0

    // 在批量模式下，更新对应文件的进度状态
    if (props.batchMode) {
      const progressItem = batchUploadProgress.value.find(item => item.name === file.name)
      if (progressItem) {
        progressItem.status = 'exception'
        progressItem.statusText = '上传失败'
        progressItem.percentage = 0
      }
    }
    return
  }

  if (props.batchMode) {
    // 批量上传模式
    const progressItem = batchUploadProgress.value.find(item => item.name === file.name)
    if (progressItem) {
      progressItem.percentage = 100
      progressItem.status = 'success'
      progressItem.statusText = '上传成功'
    }

    // 添加到批量文件列表
    batchFileList.value.push({
      url: res.data,
      name: file.name,
      size: file.size,
      type: file.type,
      // 本地上传成功后把父组件提供的索引描述带回业务侧。
      videoDescription: props.videoDescription || ''
    })

    // 检查是否所有文件都上传完成
    const allCompleted = batchUploadProgress.value.every(item =>
      item.status === 'success' || item.status === 'exception'
    )

    if (allCompleted) {
      loading.value = false

      // 统计成功和取消的文件数量
      const successCount = batchUploadProgress.value.filter(item => item.status === 'success').length
      const cancelledCount = batchUploadProgress.value.filter(item => item.status === 'exception').length
      const totalCount = batchUploadProgress.value.length

      // 只有当有文件成功上传时才触发事件
      if (successCount > 0) {
        // 如果有文件被取消，显示相应的提示
        if (cancelledCount > 0) {
          ElMessage.warning(`批量上传完成，成功上传 ${successCount} 个文件，${cancelledCount} 个文件被取消`)
        } else {
          ElMessage.success(`批量上传完成，成功上传 ${successCount} 个文件`)
        }

        // 触发批量上传成功事件
        emit('batch-upload-success', {
          batchList: batchFileList.value,
          type: 'local',
          successCount: successCount,
          cancelledCount: cancelledCount,
          totalCount: totalCount
        })
      } else {
        // 所有文件都被取消，不触发成功事件
        ElMessage.info('所有文件上传都已取消')
      }
    }
  } else {
    // 单文件上传模式
    uploadPercent.value = 100
    loading.value = false
    setTimeout(() => {
      uploadPercent.value = 0
    }, 500)

    // 使用 COS 上传的响应数据
    fileUrl.value = res.data
    // 发送文件名事件，使用COS URL（完整链接）
    console.log('Upload组件发送文件名事件:', res.data, '来源: 本地上传')
    emit('file-name-change', { fileName: res.data, source: 'local' })
    ElMessage.success('文件上传成功')
  }
}

// 调整云盘弹窗位置的方法
const adjustCloudDialogPosition = () => {
  const cloudDialog = document.querySelector('.cloud-file-dialog .el-dialog')
  if (cloudDialog) {
    // 重置定位，确保弹窗在屏幕中央
    cloudDialog.style.position = 'fixed'
    cloudDialog.style.top = '15vh'
    cloudDialog.style.left = '50%'
    cloudDialog.style.transform = 'translateX(-50%)'
    cloudDialog.style.zIndex = '3000'
  }
}

// 云盘文件选择相关方法
const openCloudFileDialog = async () => {
  if (!props.enableCloudSelect) {
    ElMessage.warning('云盘选择功能未启用')
    return
  }

  cloudFileDialogVisible.value = true
  selectedCloudFileInDialog.value = null
  processingFileId.value = null // 重置正在处理的文件ID
  cloudVideoDurationMap.value = {}

  // 延迟调整弹窗位置，确保在父对话框拖动后也能正确显示
  setTimeout(() => {
    adjustCloudDialogPosition()
  }, 100)

  try {
    const res = await getUserFolderList()
    cloudFolderList.value = res.list || []

    if (cloudFolderList.value.length > 0) {
      const firstFolder = findFirstValidFolder(cloudFolderList.value)
      if (firstFolder) {
        currentCloudFolderId.value = firstFolder.id
        currentCloudFolderName.value = firstFolder.folderName
        await fetchCloudFileList(firstFolder.id, 1, cloudPageSize.value)
      }
    }
  } catch (e) {
    ElMessage.error('获取文件夹列表失败')
    cloudFolderList.value = []
  }
}

const findFirstValidFolder = (folders) => {
  for (const folder of folders) {
    if (folder.id) return folder
    if (folder.children && folder.children.length > 0) {
      const found = findFirstValidFolder(folder.children)
      if (found) return found
    }
  }
  return null
}

const handleCloudNodeClick = async (data) => {
  if (data && data.id) {
    currentCloudFolderId.value = data.id
    currentCloudFolderName.value = data.folderName
    cloudCurrentPage.value = 1
    await fetchCloudFileList(data.id, 1, cloudPageSize.value)
  }
}

const fetchCloudFileList = async (folderId, page = 1, limit = 20) => {
  if (!folderId) {
    cloudFileList.value = []
    cloudFileTotal.value = 0
    return
  }

  cloudFileLoading.value = true
  try {
    const res = await getFilesByFolder(folderId, page, limit, cloudFileTypeFilter.value || undefined)
    cloudFileList.value = res.list || []
    cloudFileTotal.value = res.total || 0
    cloudCurrentPage.value = page
    seedCloudVideoDurations(cloudFileList.value)
  } catch (e) {
    cloudFileList.value = []
    cloudFileTotal.value = 0
    ElMessage.error('获取文件列表失败')
  } finally {
    cloudFileLoading.value = false
  }
}

const handleCloudFileSelect = async (file) => {
  if (props.batchMode) {
    // 批量模式下，通过表格选择处理
    return
  }
  if (props.autoConfirmCloudSelect) {
    if (processingFileId.value || loading.value) return
    await handleSingleFileSelect(file)
    return
  }
  selectedCloudFileInDialog.value = file
}

// 点击云盘视频缩略图打开视频预览（在云盘弹窗内层显示，无需第二个弹窗）
const openCloudVideoPreview = (fileUrl) => {
  if (!fileUrl) return
  cloudVideoPreviewUrl.value = fileUrl
  cloudVideoPreviewVisible.value = true
}
const closeCloudVideoPreview = () => {
  cloudVideoPreviewVisible.value = false
  cloudVideoPreviewUrl.value = ''
}

// 批量选择云盘文件
const handleCloudFileSelectionChange = (selection) => {
  if (props.batchMode) {
    const validFiles = selection.filter(file => isValidFile(file.fileName))

    // 检查选择的文件数量是否超过限制
    if (validFiles.length > props.fileLimit) {
      ElMessage.error(`文件数量不能超过 ${props.fileLimit} 个，当前已选择 ${validFiles.length} 个文件`)
      // 取消超出限制的选择，只保留前 fileLimit 个
      const allowedFiles = validFiles.slice(0, props.fileLimit)
      selectedCloudFiles.value = allowedFiles
      // 更新表格选中状态
      if (cloudFileTableRef.value) {
        cloudFileTableRef.value.clearSelection()
        allowedFiles.forEach(file => {
          cloudFileTableRef.value.toggleRowSelection(file, true)
        })
      }
      return
    }

    selectedCloudFiles.value = validFiles
    console.log('云盘文件选择变化，已选择文件数量:', selectedCloudFiles.value.length)
    console.log('已选择的文件:', selectedCloudFiles.value.map(f => f.fileName))
  }
}

// 获取表格行的类名，用于禁用不符合条件的文件
const getRowClassName = ({ row }) => {
  if (!isValidFile(row.fileName)) {
    return 'file-disabled-row'
  }
  return ''
}

const audioExtensions = ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a', 'wma', 'opus']
const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', '3gp', 'ogv']
const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
const isAudioFileType = (fileName = '') => {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  return audioExtensions.includes(ext)
}

const isVideoFileType = (fileName = '') => {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  return videoExtensions.includes(ext)
}

const isImageFileType = (fileName = '') => {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  return imageExtensions.includes(ext)
}

const parseDurationFromVideoDescription = (value) => {
  const text = String(value || '').trim()
  if (!text) {
    return null
  }
  try {
    const parsed = JSON.parse(text)
    const seconds = Number(parsed?.duration_seconds ?? parsed?.duration)
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null
  } catch {
    return null
  }
}

const seedCloudVideoDurations = (list = []) => {
  const nextMap = { ...cloudVideoDurationMap.value }
  for (const file of list) {
    if (!file?.id || !isVideoFileType(file.fileName)) {
      continue
    }
    const cached = parseDurationFromVideoDescription(file.videoDescription)
    if (cached != null) {
      nextMap[file.id] = cached
    } else if (nextMap[file.id] == null) {
      delete nextMap[file.id]
    }
  }
  cloudVideoDurationMap.value = nextMap
}

const handleCloudThumbMetadata = (file, event) => {
  if (!file?.id || !isVideoFileType(file.fileName)) {
    return
  }
  const duration = Number(event?.target?.duration)
  if (!Number.isFinite(duration) || duration <= 0) {
    return
  }
  if (cloudVideoDurationMap.value[file.id] === duration) {
    return
  }
  cloudVideoDurationMap.value = {
    ...cloudVideoDurationMap.value,
    [file.id]: duration
  }
}

const formatCloudVideoDuration = (file) => {
  if (!isVideoFileType(file?.fileName)) {
    return '-'
  }
  const duration = cloudVideoDurationMap.value[file.id]
  if (typeof duration === 'number' && duration > 0) {
    return `${duration.toFixed(2)}s`
  }
  return file?.fileUrl ? '检测中...' : '-'
}

const handleCloudFileTypeFilterChange = () => {
  if (cloudFileTableRef.value) {
    cloudFileTableRef.value.clearSelection()
  }
  cloudCurrentPage.value = 1
  fetchCloudFileList(currentCloudFolderId.value, 1, cloudPageSize.value)
}

// 处理单个文件选择（兼容批量模式和单文件模式）
const handleSingleFileSelect = async (file) => {
  if (!file || !isValidFile(file.fileName)) {
    ElMessage.warning('请选择有效的文件')
    return
  }

  if (props.batchMode) {
    // 批量模式下，将文件添加到选择列表并自动选中表格行
    const isAlreadySelected = selectedCloudFiles.value.some(selected => selected.id === file.id)
    if (!isAlreadySelected) {
      selectedCloudFiles.value.push(file)
      // 自动选中表格行（这会触发 handleCloudFileSelectionChange）
      if (cloudFileTableRef.value) {
        cloudFileTableRef.value.toggleRowSelection(file, true)
      }
      ElMessage.success(`已添加 "${file.fileName}" 到选择列表`)
    } else {
      ElMessage.info(`"${file.fileName}" 已在选择列表中`)
    }
  } else {
    // 单文件模式下，直接确认选择
    // 设置正在处理的文件ID，用于显示按钮loading状态
    processingFileId.value = file.id
    // 显示提示信息
    ElMessage.info('正在获取文件...')
    try {
      await confirmSelectCloudFile(file)
    } finally {
      // 清除正在处理的文件ID
      processingFileId.value = null
    }
  }
}

const confirmSelectCloudFile = async (file) => {
  if (!file || !isValidFile(file.fileName)) {
    ElMessage.warning('请选择有效的文件')
    return
  }

  try {
    loading.value = true

      const res = await downloadUserFile(file.id)
      if (res && res.url) {
        selectedCloudFile.value = file
        fileUrl.value = res.url
        // 发送云盘文件的COS URL事件（完整链接）
        console.log('Upload组件发送云盘文件名事件:', res.url, '来源: 云盘选择')
        emit('file-name-change', { fileName: res.url, source: 'cloud' })
        cloudFileDialogVisible.value = false
        ElMessage.success('文件选择成功')
      } else {
        ElMessage.error('获取文件地址失败')
      }
  } catch (e) {
    ElMessage.error('选择文件失败: ' + (e.message || e))
  } finally {
    loading.value = false
  }
}

// 批量确认选择云盘文件
const confirmBatchSelectCloudFiles = async () => {
  console.log('开始批量选择云盘文件，已选择文件数量:', selectedCloudFiles.value.length)
  console.log('已选择的文件:', selectedCloudFiles.value.map(f => f.fileName))

  if (selectedCloudFiles.value.length === 0) {
    ElMessage.warning('请选择至少一个文件')
    return
  }

  // 检查文件数量是否超过限制
  if (selectedCloudFiles.value.length > props.fileLimit) {
    ElMessage.error(`文件数量不能超过 ${props.fileLimit} 个，当前已选择 ${selectedCloudFiles.value.length} 个文件`)
    return
  }

  try {
    loading.value = true
    const batchResults = []

    for (const file of selectedCloudFiles.value) {
      console.log(`正在获取文件 ${file.fileName} 的下载地址...`)
      try {
        const res = await downloadUserFile(file.id)
        console.log(`文件 ${file.fileName} 下载地址获取结果:`, res)
        if (res && res.url) {
          batchResults.push({
            url: res.url,
            name: file.fileName,
            size: file.fileSize,
            type: file.fileName.toLowerCase().endsWith('.mp4') ? 'video/mp4' : 'audio/wav',
            // 云盘已有素材若已经缓存索引，直接回传给自动任务复用。
            videoDescription: file.videoDescription || '',
            cloudFile: file
          })
          console.log(`文件 ${file.fileName} 已添加到结果列表`)
        } else {
          console.warn(`文件 ${file.fileName} 获取地址失败，响应:`, res)
        }
      } catch (e) {
        console.error(`获取文件 ${file.fileName} 地址失败:`, e)
      }
    }

    if (batchResults.length > 0) {
      batchFileList.value = batchResults
      selectedCloudFiles.value = batchResults.map(item => item.cloudFile)
      cloudFileDialogVisible.value = false

      // 统计成功和失败的文件数量
      const successCount = batchResults.length
      const failedCount = selectedCloudFiles.value.length - successCount

      if (failedCount > 0) {
        ElMessage.warning(`成功选择 ${successCount} 个文件，${failedCount} 个文件获取地址失败`)
      } else {
        ElMessage.success(`成功选择 ${successCount} 个文件`)
      }

      emit('batch-upload-success', {
        batchList: batchResults,
        type: 'cloud',
        successCount: successCount,
        cancelledCount: 0, // 云盘选择没有取消的概念
        totalCount: selectedCloudFiles.value.length
      })
    } else {
      ElMessage.error('获取文件地址失败')
    }
  } catch (e) {
    ElMessage.error('批量选择文件失败: ' + (e.message || e))
  } finally {
    loading.value = false
  }
}

const clearSelectedCloudFile = () => {
  selectedCloudFile.value = null
  fileUrl.value = ''
}

const removeFile = () => {
  if (props.batchMode) {
    batchFileList.value = []
    batchUploadProgress.value = []
    selectedCloudFiles.value = []
    uploadRef.value?.clearFiles()
  } else {
    fileUrl.value = ''
    selectedCloudFile.value = null
    urlInput.value = ''
    videoW.value = 0
    videoH.value = 0
    // 清除el-upload组件的文件列表
    if (uploadRef.value) {
      uploadRef.value.clearFiles()
    }
  }
}

// 取消单个文件上传
const cancelSingleUpload = (item, index) => {
  console.log('取消单个文件上传:', item.name)

  // 如果有中止函数，调用它
  if (item.abortFunction) {
    try {
      item.abortFunction()
      console.log(`已中止文件上传: ${item.name}`)
    } catch (e) {
      console.error(`中止文件上传失败: ${item.name}`, e)
    }
  }

  // 更新进度项状态
  item.status = 'exception'
  item.statusText = '上传已取消'
  item.percentage = 0

  // 清除请求信息
  item.requestId = null
  item.abortFunction = null

  // 检查是否所有文件都已完成（成功或取消）
  const allCompleted = batchUploadProgress.value.every(progressItem =>
    progressItem.status === 'success' || progressItem.status === 'exception'
  )

  if (allCompleted) {
    loading.value = false

    // 统计成功和取消的文件数量
    const successCount = batchUploadProgress.value.filter(progressItem => progressItem.status === 'success').length
    const cancelledCount = batchUploadProgress.value.filter(progressItem => progressItem.status === 'exception').length
    const totalCount = batchUploadProgress.value.length

    // 只有当有文件成功上传时才触发事件
    if (successCount > 0) {
      // 如果有文件被取消，显示相应的提示
      if (cancelledCount > 0) {
        ElMessage.warning(`批量上传完成，成功上传 ${successCount} 个文件，${cancelledCount} 个文件被取消`)
      } else {
        ElMessage.success(`批量上传完成，成功上传 ${successCount} 个文件`)
      }

      // 触发批量上传成功事件
      emit('batch-upload-success', {
        batchList: batchFileList.value,
        type: 'local',
        successCount: successCount,
        cancelledCount: cancelledCount,
        totalCount: totalCount
      })
    } else {
      // 所有文件都被取消，不触发成功事件
      ElMessage.info('所有文件上传都已取消')
    }
  }

  ElMessage.info(`已取消文件 "${item.name}" 的上传`)
}

// 终止所有上传请求
const abortAllUploads = () => {
  console.log('终止所有上传请求，当前活跃请求数量:', activeUploadRequests.value.size)

  // 遍历所有活跃的上传请求并中止它们
  for (const [requestId, xhr] of activeUploadRequests.value) {
    try {
      xhr.abort()
      console.log(`已中止上传请求: ${requestId}`)
    } catch (e) {
      console.error(`中止上传请求失败: ${requestId}`, e)
    }
  }

  // 清空活跃请求Map
  activeUploadRequests.value.clear()

  // 重置状态
  loading.value = false
  uploadPercent.value = 0

  // 如果是批量模式，重置批量上传进度
  if (props.batchMode) {
    batchUploadProgress.value.forEach(item => {
      if (item.status === 'uploading') {
        item.status = 'exception'
        item.statusText = '上传已取消'
        item.requestId = null
        item.abortFunction = null
      }
    })
  }

  ElMessage.info('已取消所有上传任务')
}

// 视频元数据加载处理
const onVideoLoadedMetadata = (event) => {
  videoW.value = event.target.videoWidth
  videoH.value = event.target.videoHeight
}

const handleCloudPageChange = (page) => {
  cloudCurrentPage.value = page
  fetchCloudFileList(currentCloudFolderId.value, page, cloudPageSize.value)
}

const handleCloudPageSizeChange = (size) => {
  cloudPageSize.value = size
  cloudCurrentPage.value = 1
  fetchCloudFileList(currentCloudFolderId.value, 1, size)
}

// 工具函数
const formatSize = (bytes) => {
  if (!bytes) return '0B'
  if (bytes >= 1024 * 1024 * 1024) {
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + 'GB'
  } else if (bytes >= 1024 * 1024) {
    return (bytes / (1024 * 1024)).toFixed(2) + 'MB'
  } else if (bytes >= 1024) {
    return (bytes / 1024).toFixed(2) + 'KB'
  }
  return bytes + 'B'
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleString()
}

// 暴露方法给父组件
defineExpose({
  clearFile: removeFile,
  setFileUrl: (url) => { fileUrl.value = url },
  getSelectedCloudFile: () => selectedCloudFile.value,
  getVideoDimensions: () => ({ width: videoW.value, height: videoH.value }),
  getVideoContainerRef: () => videoContainerRef.value,
  // 批量操作相关方法
  getBatchFileList: () => batchFileList.value,
  setBatchFileList: (list) => {
    batchFileList.value = list
    // 父组件删除批量文件后清理 el-upload 内部列表，避免补传时仍被旧文件占用数量上限。
    uploadRef.value?.clearFiles()
    batchUploadProgress.value = []
  },
  getBatchUploadProgress: () => batchUploadProgress.value,
  clearBatchFiles: () => {
    batchFileList.value = []
    batchUploadProgress.value = []
    selectedCloudFiles.value = []
    uploadRef.value?.clearFiles()
  },
  // 上传控制方法
  abortAllUploads: abortAllUploads
})
</script>

<style scoped>
.upload-component {
  width: 100%;
}

/* 上传模式选择器样式 */
.upload-mode-selector {
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #F9FAFB;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
}

/* 上传区域样式 */
.upload-area {
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
}

.upload-demo {
  width: 100%;
}

/* 云盘文件选择器样式 */
.cloud-file-selector {
  padding: 16px;
  background: #F9FAFB;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
}

.cloud-selector-header {
  display: flex;
  align-items: center;
}

/* 网络连接输入区域样式 */
.url-input-area {
  padding: 16px;
  background: #F9FAFB;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
}

.url-input-container {
  width: 100%;
}

.selected-file-info {
  padding: 12px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
}

/* 文件预览样式 */
.file-preview {
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
  background: #fff;
}

.default-preview {
  display: flex;
  align-items: center;
}

/* 云盘文件选择弹窗样式 */
.cloud-file-dialog .el-dialog__body {
  padding: 0;
  max-height: 85vh;
  overflow: hidden;
}

.cloud-file-dialog .el-dialog {
  margin: 0 !important;
  position: fixed !important;
  top: 10vh !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  max-height: 80vh !important;
  z-index: 3000 !important;
  width: 85% !important;
}

.cloud-file-dialog-content {
  position: relative;
  display: flex;
  height: 70vh;
  overflow: hidden;
}

/* 视频预览遮罩层 */
.cloud-video-preview-inner {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.cloud-video-preview-inner-box {
  background: #fff;
  padding: 20px;
  border-radius: 16px;
  max-width: 90%;
  max-height: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}
.cloud-video-preview-inner-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 14px;
}

/* 确保云盘弹窗始终在屏幕中央 */
.cloud-file-dialog {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 3000 !important;
}

.cloud-folder-tree {
  width: 260px;
  min-width: 220px;
  background: #F3F4F6;
  border-right: 1px solid #E5E7EB;
  padding: 16px;
  overflow-y: auto;
  max-height: 70vh;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #D1D5DB;
    border-radius: 2px;
  }

  /* 文件夹区块标题 */
  .folder-section-label {
    font-size: 12px;
    color: #9CA3AF;
    margin-bottom: 8px;
    flex-shrink: 0;
  }

  /* 文件夹列表滚动区 */
  .folder-list {
    flex: 1;
    overflow-y: auto;
  }

  /* 空文件夹状态 */
  .empty-folder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 160px;
    color: #9CA3AF;

    .empty-text {
      margin-top: 12px;
      font-size: 14px;
      color: #9CA3AF;
    }
  }

  /* el-tree 节点样式 */
  :deep(.el-tree) {
    background: transparent;
    color: #1F2937;

    .el-tree-node__content {
      height: 36px;
      border-radius: 6px;
      transition: background 0.15s;

      &:hover {
        background: #E5E7EB;
      }
    }

    .el-tree-node.is-current > .el-tree-node__content {
      background: #E0E7FF;
      color: #4F46E5;
      font-weight: 600;
    }
  }
}

.cloud-file-list {
  flex: 1;
  padding: 16px;
  background: #fff;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #E5E7EB;
    border-radius: 2px;
  }
}

.cloud-thumb {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  background: #F3F4F6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid #E5E7EB;
}

.cloud-thumb-video-wrap {
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.cloud-thumb video,
.cloud-thumb-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cloud-thumb-icon {
  color: var(--text-secondary);
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.table-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.cloud-file-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.cloud-pagination {
  margin-top: 12px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

.empty-folder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--text-secondary);
}

.empty-text {
  margin-top: 12px;
  font-size: 14px;
  color: var(--text-secondary);
}

/* 文件禁用状态样式 */
:deep(.file-disabled-row) {
  opacity: 0.5;
  color: var(--text-secondary);
  cursor: not-allowed;
}

:deep(.file-disabled-row:hover) {
  background-color: transparent !important;
}

:deep(.file-disabled-row .el-button) {
  opacity: 0.3;
  cursor: not-allowed;
}

/* 视频预览样式 */
.video-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.video-preview-relative {
  position: relative;
  width: 100%;
}

/* 批量上传进度样式 */
.batch-upload-progress {
  margin-top: 16px;
  padding: 16px;
  background: #F9FAFB;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
}

.batch-upload-progress h4 {
  margin: 0 0 12px 0;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
}

.progress-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 10px 12px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #E5E7EB;
}

.progress-item:last-child {
  margin-bottom: 0;
}

.filename {
  flex: 1;
  color: var(--text-primary);
  font-size: 12px;
  margin-right: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-text {
  margin-left: 12px;
  font-size: 12px;
  color: var(--text-secondary);
  min-width: 60px;
  text-align: right;
}

.cancel-btn {
  margin-left: 8px;
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cloud-file-type-filters .el-radio-group .el-radio-button__inner {
  color: var(--text-secondary, #6B7280);
}

.cloud-file-type-filters .el-radio-group .el-radio-button--small .el-radio-button__inner {
  padding: 5px 12px;
}

.cloud-file-type-filters .el-radio-group .el-radio-button.is-active .el-radio-button__inner {
  color: #fff;
}
</style>
